import React, { useState, useEffect } from 'react';
import { contractorsAPI, tradeAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Edit, Trash2, Phone, Mail, Users, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

const tradeTypes = [
  'General', 'Plumber', 'Electrician', 'Carpenter', 'Painter', 'Tiler', 
  'Roofer', 'Plasterer', 'Bricklayer', 'Concreter', 'Landscaper',
  'HVAC', 'Glazier', 'Fencer', 'Flooring', 'Other'
];

const qldRegions = ['Brisbane', 'Gold Coast', 'Wide Bay'];

export const ContractorsPage = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    trade_types: [],
    regions: [],
    email: '',
    phone: '',
    company_name: '',
    address: '',
    hourly_rate: '',
    notes: '',
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await contractorsAPI.getAll();
      setContractors(response.data);
    } catch (error) {
      toast.error('Failed to fetch contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleTradeToggle = (trade) => {
    setFormData(prev => ({
      ...prev,
      trade_types: prev.trade_types.includes(trade)
        ? prev.trade_types.filter(t => t !== trade)
        : [...prev.trade_types, trade]
    }));
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.trade_types.length === 0) {
      toast.error('Please select at least one trade type');
      return;
    }
    try {
      const data = {
        ...formData,
        trade_type: formData.trade_types[0], // For backwards compatibility
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      };

      if (editingContractor) {
        await contractorsAPI.update(editingContractor.id, data);
        toast.success('Contractor updated');
      } else {
        await contractorsAPI.create(data);
        toast.success('Contractor created');
      }
      
      fetchContractors();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setFormData({
      name: contractor.name,
      trade_types: contractor.trade_types || (contractor.trade_type ? [contractor.trade_type] : []),
      regions: contractor.regions || [],
      email: contractor.email,
      phone: contractor.phone,
      company_name: contractor.company_name || '',
      address: contractor.address || '',
      hourly_rate: contractor.hourly_rate?.toString() || '',
      notes: contractor.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) return;
    try {
      await contractorsAPI.delete(id);
      toast.success('Contractor deleted');
      fetchContractors();
    } catch (error) {
      toast.error('Failed to delete contractor');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingContractor(null);
    setFormData({
      name: '',
      trade_types: [],
      regions: [],
      email: '',
      phone: '',
      company_name: '',
      address: '',
      hourly_rate: '',
      notes: '',
    });
  };

  const getContractorTrades = (contractor) => {
    if (contractor.trade_types && contractor.trade_types.length > 0) {
      return contractor.trade_types;
    }
    return contractor.trade_type ? [contractor.trade_type] : [];
  };

  const filteredContractors = contractors.filter(c => {
    const trades = getContractorTrades(c);
    return (
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trades.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.regions || []).some(r => r.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Contractors</h1>
          <p className="text-muted-foreground mt-1">Manage your trades and contractors</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="add-contractor-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Contractor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContractor ? 'Edit Contractor' : 'Add Contractor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-input border-border rounded-sm"
                    data-testid="contractor-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="bg-input border-border rounded-sm"
                  />
                </div>
              </div>

              {/* Trade Types - Multi-select */}
              <div className="space-y-2">
                <Label>Trade Types * <span className="text-xs text-muted-foreground">(Select all that apply)</span></Label>
                <div className="grid grid-cols-4 gap-2 p-3 bg-input/50 rounded-sm border border-border">
                  {tradeTypes.map(trade => (
                    <div key={trade} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`trade-${trade}`}
                        checked={formData.trade_types.includes(trade)}
                        onCheckedChange={() => handleTradeToggle(trade)}
                      />
                      <label htmlFor={`trade-${trade}`} className="text-sm cursor-pointer">{trade}</label>
                    </div>
                  ))}
                </div>
                {formData.trade_types.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.trade_types.map(trade => (
                      <Badge key={trade} variant="secondary" className="rounded-sm">
                        {trade}
                        <button type="button" onClick={() => handleTradeToggle(trade)} className="ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Regions - Multi-select */}
              <div className="space-y-2">
                <Label>Regions <span className="text-xs text-muted-foreground">(QLD)</span></Label>
                <div className="flex gap-4 p-3 bg-input/50 rounded-sm border border-border">
                  {qldRegions.map(region => (
                    <div key={region} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`region-${region}`}
                        checked={formData.regions.includes(region)}
                        onCheckedChange={() => handleRegionToggle(region)}
                      />
                      <label htmlFor={`region-${region}`} className="text-sm cursor-pointer">{region}</label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-input border-border rounded-sm"
                    data-testid="contractor-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="bg-input border-border rounded-sm"
                    data-testid="contractor-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    className="bg-input border-border rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-input border-border rounded-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-sm"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" data-testid="contractor-submit">
                  {editingContractor ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search contractors..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-sm"
          data-testid="contractor-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredContractors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Users className="h-12 w-12 mb-4" />
              <p>No contractors found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Trades</TableHead>
                  <TableHead>Regions</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContractors.map((contractor) => {
                  const trades = getContractorTrades(contractor);
                  return (
                    <TableRow key={contractor.id} className="border-border" data-testid={`contractor-row-${contractor.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{contractor.name}</p>
                          {contractor.company_name && (
                            <p className="text-xs text-muted-foreground">{contractor.company_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {trades.map(trade => (
                            <Badge key={trade} variant="outline" className="rounded-sm font-mono text-xs uppercase">
                              {trade}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(contractor.regions || []).map(region => (
                            <Badge key={region} variant="secondary" className="rounded-sm text-xs">
                              <MapPin className="h-3 w-3 mr-1" />
                              {region}
                            </Badge>
                          ))}
                          {(!contractor.regions || contractor.regions.length === 0) && (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <a href={`mailto:${contractor.email}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Mail className="h-3 w-3" />
                            {contractor.email}
                          </a>
                          <a href={`tel:${contractor.phone}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Phone className="h-3 w-3" />
                            {contractor.phone}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        {contractor.hourly_rate ? (
                          <span className="font-mono">${contractor.hourly_rate}/hr</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(contractor)} data-testid={`edit-contractor-${contractor.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(contractor.id)} className="text-destructive" data-testid={`delete-contractor-${contractor.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractorsPage;
